import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, Loader2, ArrowRight, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiErr } from "@/lib/api";

const usd = (n) => `$ ${Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pen = (n) => `S/ ${Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PriceDiffDialog = ({ quoteId, open, onOpenChange, isAdmin, onUpdated, unsavedWarning }) => {
  const [diff, setDiff] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { return; }
    setDiff(null);
    api.get(`/quotes/${quoteId}/price-diff`).then((r) => setDiff(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [open, quoteId]);

  const update = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${quoteId}/refresh-prices`);
      toast.success(`Precios actualizados (${data.updated} característica(s))`);
      onOpenChange(false);
      onUpdated?.();
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };

  const delta = diff ? diff.venta_new - diff.venta_old : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="price-diff-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-600" /> Cambios de precio del catálogo</DialogTitle></DialogHeader>
        {!diff ? (
          <div className="text-slate-400 flex items-center gap-2 py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Calculando...</div>
        ) : (
          <div className="space-y-5 text-sm">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Características con precio distinto</p>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="price-diff-changes">
                {diff.changes.map((c) => (
                  <div key={c.name} className="flex items-center justify-between px-3 py-2">
                    <span className="text-slate-800">{c.name}</span>
                    {isAdmin && (
                      <span className="font-mono text-xs flex items-center gap-1.5">
                        <span className="text-slate-400 line-through">{usd(c.old)}</span><ArrowRight className="h-3 w-3 text-slate-400" />
                        <span className={c.new > c.old ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>{usd(c.new)}</span>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">Puertas afectadas ({diff.doors.length})</p>
              <table className="w-full text-xs border border-slate-200 rounded-md" data-testid="price-diff-doors">
                <thead><tr className="bg-slate-50 text-slate-500 uppercase">
                  <th className="px-3 py-1.5 text-left">Código</th><th className="px-3 py-1.5 text-right">Cant.</th>
                  <th className="px-3 py-1.5 text-left">Cambia</th>{isAdmin && <th className="px-3 py-1.5 text-right">FOB unit.</th>}
                </tr></thead>
                <tbody>
                  {diff.doors.map((d) => (
                    <tr key={d.door_id} className="border-t border-slate-100">
                      <td className="px-3 py-1.5 font-mono">{d.code || "—"}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{d.qty}</td>
                      <td className="px-3 py-1.5 text-slate-600">{d.features.join(", ")}</td>
                      {isAdmin && <td className="px-3 py-1.5 text-right font-mono whitespace-nowrap">{usd(d.fob_old)} → <span className="font-semibold">{usd(d.fob_new)}</span></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-md px-4 py-3 flex flex-wrap items-center justify-between gap-2" data-testid="price-diff-total">
              <span className="text-slate-500">Venta total</span>
              <span className="font-mono flex items-center gap-2">
                <span className="text-slate-400">{pen(diff.venta_old)}</span><ArrowRight className="h-3.5 w-3.5 text-slate-400" />
                <span className="font-bold text-slate-900">{pen(diff.venta_new)}</span>
                <span className={`text-xs font-semibold ${delta > 0 ? "text-red-600" : "text-emerald-600"}`}>({delta > 0 ? "+" : ""}{pen(delta)})</span>
              </span>
            </div>
            {isAdmin ? (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {unsavedWarning && <span className="text-xs text-amber-700 mr-auto">Los cambios sin guardar de la cotización se perderán.</span>}
                <button data-testid="price-diff-cancel" onClick={() => onOpenChange(false)}
                  className="px-4 py-2 rounded-md text-sm font-semibold text-slate-600 hover:bg-slate-100 transition-colors">Cancelar</button>
                <button data-testid="price-diff-apply" onClick={update} disabled={busy}
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors disabled:opacity-50">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Actualizar precios
                </button>
              </div>
            ) : (
              <p className="text-xs text-slate-500">Solo un administrador puede aplicar los precios nuevos.</p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
